import { forwardRef, type MouseEvent, type ReactNode } from "react";

import { PushPin } from "@mui/icons-material";
import { Box, Card, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { MediaGallery, type MediaGalleryItem } from "../MediaGallery";

/** The gallery's item, under the name this card's callers already import. */
export type MediaCardItem = MediaGalleryItem;

export interface MediaCardProps {
  /** Header title. */
  title: ReactNode;
  /** Tints the header background; falls back to a neutral grey when omitted. */
  accentColor?: string;
  /** Strikes through the title (e.g. for a closed/resolved state). */
  titleStrikethrough?: boolean;
  /** Rendered on the right of the header (e.g. a status label). */
  headerAction?: ReactNode;
  /** Rendered below the title within the header band (e.g. category tags). */
  subheader?: ReactNode;
  /** Highlights the card with a primary-colored border. */
  selected?: boolean;
  /** Marks the card as pinned/anchored: thicker border, pin icon before the title. */
  pinned?: boolean;
  /** Media shown in the hero and, when more than one, the thumbnail strip. */
  media?: MediaCardItem[];
  /** Absolutely-positioned nodes layered over the hero (badges, buttons). */
  mediaOverlay?: ReactNode;
  /** Shown in the media area when `media` is empty. */
  emptyContent?: ReactNode;
  /** Content rendered below the media; the caller composes it. */
  footer?: ReactNode;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
}

/**
 * Presentational media card: an accent header, a `MediaGallery`, and a free-form
 * footer slot. It owns no domain logic — callers map their data into
 * `media`/`title`/`footer` and decide what a click means.
 *
 * Everything about how the media behaves — lazy loading, the blurred letterbox,
 * the 404 fallback, video poster frames — belongs to the gallery, which a
 * bulletin detail page renders without a card around it. What stays here is the
 * card: the fixed media height, and the strip's click not counting as a click on
 * the card.
 */
export const MediaCard = forwardRef<HTMLDivElement, MediaCardProps>(
  function MediaCard(
    {
      title,
      accentColor,
      titleStrikethrough = false,
      headerAction,
      subheader,
      selected = false,
      pinned = false,
      media = [],
      mediaOverlay,
      emptyContent,
      footer,
      onClick,
      className,
    },
    ref,
  ) {
    return (
      <Card
        ref={ref}
        className={className}
        onClick={onClick}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          alignSelf: "stretch",
          minWidth: 260,
          maxWidth: 350,
          cursor: onClick ? "pointer" : "default",
          borderRadius: 1,
          overflow: "hidden",
          boxShadow: "var(--card-shadow)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            border: pinned ? "6px solid" : "3px solid",
            borderColor: selected ? "action.active" : "transparent",
            borderRadius: "inherit",
            pointerEvents: "none",
            zIndex: 1,
          },
          transition: "transform .15s ease-in-out, box-shadow .15s ease-in-out",
          "&:hover": { transform: "scale(1.01)", boxShadow: 12 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: accentColor ? alpha(accentColor, 0.25) : "action.hover",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0, flex: 1 }}
            >
              {pinned && <PushPin fontSize="small" sx={{ flexShrink: 0 }} />}
              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  textDecoration: titleStrikethrough ? "line-through" : "none",
                }}
              >
                {title}
              </Typography>
            </Stack>
            {headerAction}
          </Stack>
          {subheader && <Box sx={{ mt: 1 }}>{subheader}</Box>}
        </Box>

        <Stack
          className="media-card-title-rounded-corner"
          sx={{
            width: "100%",
            height: "6px",
            borderRadius: "3px",
            top: "-5px",
            position: "relative",
            backgroundColor: "background.paper",
          }}
        ></Stack>
        {/* Media section – fixed height regardless of strip presence */}
        <Box sx={{ mx: "0.5rem" }}>
          <MediaGallery
            media={media}
            height={277}
            overlay={mediaOverlay}
            emptyContent={emptyContent}
          />
        </Box>

        {/* Footer */}
        {footer && (
          <Box sx={{ px: 1.5, pt: 1, pb: 1.5, flex: 1 }}>{footer}</Box>
        )}
      </Card>
    );
  },
);
